import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pageStyles from './BranchDetail.module.css';
import tableStyles from '../components/branches/SectionTable.module.css';
import SectionTable from '../components/branches/SectionTable';
import ListPageHeader from '../components/common/ListPageHeader';
import Pill from '../components/common/Pill';
import StatusFilter from '../components/cci/StatusFilter';
import { DEFAULT_CCI_STATUSES, cciStatusKind, cciStatusLabel, shortSha, triggerLabel } from '../components/cci/cciStatus';
import { apiGetCciJobs } from '../api/cci';
import { formatDateTime } from '../helpers/dates';
import { WebSocketContext } from '../api/socket';

const PAGE_SIZE = 25;
// Live updates arrive in bursts (a poll leases several jobs); refetch the page once they settle
const LIVE_RELOAD_DELAY_MS = 1000;

const ICO_JOBS = 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11';

const COLUMNS = [
  { label: 'Job',     width: '11%' },
  { label: 'Status',  width: '10%' },
  { label: 'Trigger', width: '12%' },
  { label: 'Repository', width: '17%' },
  { label: 'PR',      width: '7%' },
  { label: 'Commit',  width: '9%' },
  { label: 'Attempt', width: '7%' },
  { label: 'Runner',  width: '14%' },
  { label: 'Created', width: '13%' },
];

function renderRow(job) {
  return (
    <>
      <td className={tableStyles.td}><span className={tableStyles.mono}>{job.job_id}</span></td>
      <td className={tableStyles.td}>
        <Pill kind={cciStatusKind(job.status)} dot pulse={job.status === 'leased'}>{cciStatusLabel(job.status)}</Pill>
      </td>
      <td className={tableStyles.td}>{triggerLabel(job.event)}</td>
      <td className={tableStyles.td}>{job.event.repo}</td>
      <td className={tableStyles.td}>
        {job.event.pr_number ? `#${job.event.pr_number}` : <span className={tableStyles.muted}>—</span>}
      </td>
      <td className={tableStyles.td}><span className={tableStyles.mono}>{shortSha(job.event.head_sha)}</span></td>
      <td className={tableStyles.td}>{job.attempt}/{job.max_attempts}</td>
      <td className={tableStyles.td}>
        {job.runner_id ? <span className={tableStyles.mono} style={{ fontSize: 12.5 }}>{job.runner_id}</span> : <span className={tableStyles.muted}>—</span>}
      </td>
      <td className={tableStyles.td}>{formatDateTime(job.created_at)}</td>
    </>
  );
}

// Not linked from the side panel on purpose: reachable at /cci while the feature is private
export default function CciJobsList({ user }) {
  const navigate = useNavigate();
  const ws = useContext(WebSocketContext);
  const [statuses, setStatuses] = useState(DEFAULT_CCI_STATUSES);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [jobs, setJobs] = useState({ loaded: false, list: [], total: 0 });
  // Only the latest request may update the table, so a slow earlier page can't overwrite a newer one
  const latestRequest = useRef(0);

  const reloadData = () => {
    const request = ++latestRequest.current;
    // No status selected means nothing to show, not "every status" as the API reads it
    if (!statuses.length) {
      setJobs({ loaded: true, list: [], total: 0 });
      return;
    }
    apiGetCciJobs({ statuses, search, limit: PAGE_SIZE, offset: page * PAGE_SIZE }).then((r) => {
      if (request === latestRequest.current) setJobs({ loaded: true, list: r.jobs, total: r.total });
    }).catch((e) => {
      if (request === latestRequest.current) setJobs({ loaded: true, list: [], total: 0, error: e });
    });
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statuses, search, page]);

  // The socket handler outlives renders, so it reaches the current filters and page through a ref
  const reloadRef = useRef(reloadData);
  reloadRef.current = reloadData;

  useEffect(() => {
    if (!ws) return undefined;
    let timer = null;
    const onJobUpdate = ({ job }) => {
      // Visible rows change right away; the debounced refetch then settles order, filters and total
      setJobs((state) => (state.list.some((j) => j.job_id === job.job_id)
        ? { ...state, list: state.list.map((j) => (j.job_id === job.job_id ? job : j)) }
        : state));
      clearTimeout(timer);
      timer = setTimeout(() => reloadRef.current(), LIVE_RELOAD_DELAY_MS);
    };
    ws.on('cci_job_update', onJobUpdate);
    return () => {
      clearTimeout(timer);
      ws.off('cci_job_update', onJobUpdate);
    };
  }, [ws]);

  const changeStatuses = (next) => { setStatuses(next); setPage(0); };
  const changeSearch = (q) => {
    if (q === search) return;
    setSearch(q);
    setPage(0);
  };

  if (!jobs.loaded) {
    return (
      <div className={pageStyles.loading}>
        <div className={pageStyles.loadingSpinner} />
        Loading jobs…
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.inner}>
        <ListPageHeader title="CI jobs" subtitle="Jobs queued from GitHub webhooks for CI runners" onReload={reloadData} />
        <StatusFilter selected={statuses} onChange={changeStatuses} />
        <SectionTable
          id="cci-jobs-table"
          icon={ICO_JOBS}
          title="Jobs"
          searchPlaceholder="Search job, repo, PR, commit, runner, action…"
          pagination="server"
          page={page}
          totalCount={jobs.total}
          onPageChange={setPage}
          onSearchChange={changeSearch}
          items={jobs.list}
          columns={COLUMNS}
          renderRow={renderRow}
          getKey={(job) => job.job_id}
          onOpenItem={(job) => navigate(`/cci/${job.job_id}`)}
          pageSize={PAGE_SIZE}
          emptyTitle={jobs.error ? 'Could not load jobs' : 'No jobs'}
          emptyHint={jobs.error ? String(jobs.error.detail ?? jobs.error) : 'No jobs match the selected statuses and search.'}
        />
      </div>
    </div>
  );
}
